import { Text } from "@solomon-labs/design/components/ui";
import React, { createContext, useContext } from "react";

import { cn } from "../../../utils/cn";

/**
 * One column in a `Table`. Alignment + responsive visibility live here as the
 * single source of truth so the header row (rendered by `Table`) and every
 * body cell (rendered by `TableRow`) stay aligned to the same grid track.
 *
 * Width is NOT a per-column prop: the `Table` lays its columns out on a shared
 * CSS-grid template (`gridClassName`, below) that changes per container
 * breakpoint, exactly like the Positions table's `ROW_GRID`. Both the header
 * and each row apply the same `grid ${gridClassName}`, so a `display:none`
 * cell drops out of the grid and the remaining cells realign to the tracks
 * listed at that breakpoint — keeping header and rows aligned for free.
 *
 * Hoisted into `shared/widgets/yaas/programs` (from the app's
 * `pages/Programs/Table`) so the shared `QualifiedUsers` View + its
 * `ParticipantRow` rows can render fixture-driven in the "+New Program"
 * wiki doc block, with no wallet / RPC / orgApiFetch. The app keeps a
 * re-export shim at the old path so `yaas-client` compiles unchanged.
 */
export interface TableColumn {
    /** Stable key — a `TableRow`'s `cells` record is keyed by this. */
    key: string;
    /** Column-header label. */
    label: React.ReactNode;
    /** Cell + header text alignment. Defaults to `"left"`. */
    align?: "left" | "right";
    /**
     * Responsive hide class(es) for progressive collapse. Applied in lockstep
     * to BOTH the header cell and every body cell for this column, so the two
     * never disagree. Uses **container queries** so the column drops by the
     * table's own width, not the viewport — the Qualified Users table lives in
     * a resizable detail pane.
     *
     * When a column is hidden its grid track must ALSO be absent from
     * `gridClassName` at that breakpoint (the grid template is the source of
     * truth for tracks); this class just removes the cell so it stops
     * occupying a track. Mirrors the Positions table's per-cell
     * `hidden @Nxl/qutable:block` + matching per-breakpoint grid template.
     */
    hideClassName?: string;
}

interface TableConfig {
    columns: TableColumn[];
    gridClassName: string;
}

const TableContext = createContext<TableConfig | null>(null);

/** Read the enclosing `Table`'s columns + grid template — used by `TableRow`. */
export function useTableConfig(): TableConfig {
    const config = useContext(TableContext);
    if (config === null) {
        throw new Error("TableRow must be rendered inside a Table");
    }
    return config;
}

/** Per-column alignment + visibility classes shared by the header + `TableRow`.
 *  `hideClassName` (container-query toggle) is applied here so the header cell
 *  and every body cell for a column hide in lockstep. Width is owned by the
 *  grid template, not the cell. */
export function columnClassName(column: TableColumn): string {
    return cn(
        "min-w-0",
        column.align === "right" ? "text-right" : "text-left",
        column.hideClassName
    );
}

export interface TableProps {
    /** Column definitions — the single source of truth for alignment + visibility. */
    columns: TableColumn[];
    /**
     * The responsive CSS-grid template (`grid-cols-[…] @Nxl/qutable:grid-cols-[…]`)
     * plus `items-center` / `gap-*`. Changes per container breakpoint so hidden
     * columns' tracks drop out and remaining cells realign — the same mechanism
     * the Positions table uses. Owned by the consumer (single source of truth
     * for widths) and applied identically to the header and every row.
     */
    gridClassName: string;
    /** The body rows — `TableRow`s that read the config from context. */
    children: React.ReactNode;
    /** Extend / override the outer wrapper chrome (vertical rhythm, dividers). */
    className?: string;
}

/**
 * Table — a generic column-defined table shell. It owns the `columns`
 * definition (alignment + visibility) and the shared responsive grid template,
 * renders the header row *from* those columns on that grid, and shares both
 * with its rows via context so header and body cells align to the same tracks.
 * The header lives here — not in a separate component — precisely because
 * `columns` + `gridClassName` are the single source of truth for every cell's
 * placement; splitting it out would force them to be repeated.
 *
 * Generic Component: it knows no Solomon/YaaS entity. Columns + grid are props
 * and the rows are `children`, so this shell never imports a Domain Component
 * (dependency firewall).
 *
 * @see TableRow — one body row; reads the config from context.
 */
export function Table({
    columns,
    gridClassName,
    children,
    className
}: TableProps): React.ReactElement {
    return (
        <TableContext.Provider value={{ columns, gridClassName }}>
            <div
                className={cn(
                    // Named container so per-column `hideClassName` toggles
                    // (`@Nxl/qutable:*`) AND the responsive grid template respond
                    // to the TABLE's own width — not the viewport — since this
                    // table lives in a resizable detail pane. Mirrors Positions'
                    // `@container/postable`.
                    "@container/qutable border-t border-line-base divide-y divide-line-base",
                    className
                )}
            >
                {/* Header row — rendered FROM columns on the shared grid (single
                    source of truth for track placement/alignment). */}
                <div
                    className={cn(
                        "grid px-4 py-2 text-foreground-muted",
                        gridClassName
                    )}
                >
                    {columns.map((column) => (
                        <Text
                            key={column.key}
                            variant="fine-print"
                            render={<span />}
                            className={columnClassName(column)}
                        >
                            {column.label}
                        </Text>
                    ))}
                </div>
                {children}
            </div>
        </TableContext.Provider>
    );
}
